/**
 * SkeletonShimmer Component
 *
 * Animated shimmer effect for skeleton loading states.
 * Pulses opacity from 0.3 to 0.7 in a continuous loop.
 *
 * @module components/SkeletonShimmer
 */

import { useEffect, useRef } from "react"
import { Animated, Platform, ViewStyle } from "react-native"

import { colors } from "@/theme"

export interface SkeletonShimmerProps {
  /**
   * Width of the skeleton element
   */
  width?: number | string

  /**
   * Height of the skeleton element
   */
  height?: number | string

  /**
   * Border radius
   */
  borderRadius?: number

  /**
   * Optional style override
   */
  style?: ViewStyle
}

/**
 * SkeletonShimmer Component
 *
 * Creates an animated placeholder element with pulsing opacity effect.
 *
 * @example
 * ```tsx
 * <SkeletonShimmer width={200} height={24} borderRadius={6} />
 * <SkeletonShimmer width="100%" height={16} borderRadius={4} />
 * ```
 */
export function SkeletonShimmer({
  width = "100%",
  height = 20,
  borderRadius = 4,
  style: $styleOverride,
}: SkeletonShimmerProps) {
  const shimmerOpacity = useRef(new Animated.Value(0.3)).current

  useEffect(() => {
    const shimmerAnimation = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerOpacity, {
          toValue: 0.7,
          duration: 800,
          useNativeDriver: Platform.OS !== "web",
        }),
        Animated.timing(shimmerOpacity, {
          toValue: 0.3,
          duration: 800,
          useNativeDriver: Platform.OS !== "web",
        }),
      ]),
    )

    shimmerAnimation.start()

    return () => shimmerAnimation.stop()
  }, [shimmerOpacity])

  return (
    <Animated.View
      style={[
        $shimmer,
        {
          width: width as any,
          height: height as any,
          borderRadius,
          opacity: shimmerOpacity,
        },
        $styleOverride,
      ]}
    />
  )
}

// Styles
const $shimmer: ViewStyle = {
  backgroundColor: colors.border, // Light grey for web (neutral background)
}
